import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  AlertTriangle,
  Ban,
  Building2,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Copy,
  Filter,
  GitBranch,
  Info,
  Loader2,
  Search,
  Sparkles,
  Users,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { EmptyState } from '@/components/common/EmptyState';
import { customerIntelApi, customerIntelKeys } from '@/lib/api';
import { useValidateModules } from '@/features/migrations/hooks/useValidateModules';
import { useAuth } from '@/hooks/useAuth';
import { hasPermission } from '@/types';
import type {
  AmplifyMatchWarning,
  BridgeAnalysisResult,
  BridgeEntityInfo,
  DryRunModuleResult,
  DryRunSkippedModule,
  DuplicationWarning,
  IdentityCollisionWarning,
  ProductMigratability,
  UserMigrationDryRunResult,
} from '@/types';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

type StatusFilter = 'all' | 'migratable' | 'blocked' | 'special' | 'duplicate';

interface ProductCounts {
  total: number;
  migratable: number;
  special: number;
  duplicate: number;
  blocked: number;
}

interface ValidationRow {
  status: 'pending' | 'valid' | 'invalid';
  blockers: string[];
  organizationName: string | undefined;
}

export function BridgeAnalysisTab() {
  const [uuid, setUuid] = useState<string>('');
  const [searchUuid, setSearchUuid] = useState('');
  const [uuidError, setUuidError] = useState<string | null>(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: customerIntelKeys.bridgeAnalysis(searchUuid),
    queryFn: () => customerIntelApi.getBridgeAnalysis(searchUuid),
    enabled: searchUuid.length > 0,
  });

  function handleLookup() {
    const trimmed = uuid.trim();
    if (!trimmed) {
      setUuidError('Organization UUID is required');
      return;
    }
    if (!UUID_REGEX.test(trimmed)) {
      setUuidError('Invalid UUID format');
      return;
    }
    setUuidError(null);

    // If the operator re-submits the same UUID, setSearchUuid is a no-op (React
    // skips the re-render) so the query never refires. Explicitly refetch in
    // that case — otherwise TanStack Query happily reuses the cached result.
    if (searchUuid === trimmed) {
      void refetch();
    } else {
      setSearchUuid(trimmed);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') {
      handleLookup();
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <GitBranch className="h-5 w-5" />
            Bridge Analysis
          </CardTitle>
          <CardDescription>
            Look up a Bridge organization to see which products can migrate today, spot
            duplicates against existing Amplify tenants, and run a live validation of each
            module against its source system.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <Label htmlFor="bridge-org-uuid">Organization UUID</Label>
              <Input
                id="bridge-org-uuid"
                placeholder="e.g. a1b2c3d4-e5f6-7890-abcd-ef1234567890"
                value={uuid}
                onChange={(e) => setUuid(e.target.value)}
                onKeyDown={handleKeyDown}
                className="font-mono"
              />
            </div>
            <Button onClick={handleLookup} disabled={isLoading}>
              {isLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Search className="h-4 w-4 mr-2" />
              )}
              {isLoading ? 'Analyzing…' : 'Analyze'}
            </Button>
          </div>
          {uuidError && <p className="text-sm text-destructive mt-2">{uuidError}</p>}
        </CardContent>
      </Card>

      {isError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Lookup failed</AlertTitle>
          <AlertDescription>
            {(error as Error)?.message ?? 'Failed to fetch Bridge analysis'}
          </AlertDescription>
        </Alert>
      )}

      {data && !data.found && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertTitle>Organization not found</AlertTitle>
          <AlertDescription>
            No Bridge entity matches that UUID. Double-check the value and try again.
          </AlertDescription>
        </Alert>
      )}

      {data?.found && <BridgeAnalysisResults data={data} />}
    </div>
  );
}

function BridgeAnalysisResults({ data }: { data: BridgeAnalysisResult }) {
  const { user } = useAuth();
  const canValidate = (user?.roles ?? []).some((role) => hasPermission(role, 'write'));

  const [filter, setFilter] = useState<StatusFilter>('all');
  const [validation, setValidation] = useState<Record<string, ValidationRow>>({});

  const counts = useMemo<ProductCounts>(() => ({
    total: data.products.length,
    migratable: data.products.filter((p) => p.migratabilityStatus === 'Yes').length,
    special: data.products.filter((p) => p.migratabilityStatus === 'Special').length,
    duplicate: data.products.filter((p) => p.migratabilityStatus === 'Duplicate').length,
    blocked: data.products.filter((p) => p.migratabilityStatus === 'No').length,
  }), [data.products]);

  const filteredProducts = useMemo(() => {
    const target = filterToStatus(filter);
    return target ? data.products.filter((p) => p.migratabilityStatus === target) : data.products;
  }, [filter, data.products]);

  const validatableProducts = useMemo(
    () => data.products.filter(isValidatable),
    [data.products],
  );
  const validatableModuleKeys = useMemo(
    () => new Set(validatableProducts.map((p) => p.moduleKey ?? '')),
    [validatableProducts],
  );

  const validateMutation = useValidateModules({
    onSuccess: (response) => {
      setValidation((prev) => {
        const next = { ...prev };
        for (const r of response.results) {
          next[r.moduleKey] = {
            status: r.isValid ? 'valid' : 'invalid',
            blockers: r.blockers,
            organizationName: r.organizationName,
          };
        }
        return next;
      });
    },
    onError: () => {
      setValidation((prev) => {
        const next = { ...prev };
        for (const p of validatableProducts) {
          const key = p.moduleKey;
          if (key && next[key]?.status === 'pending') {
            delete next[key];
          }
        }
        return next;
      });
    },
  });

  function runValidation() {
    if (validatableProducts.length === 0) return;
    setValidation((prev) => {
      const next = { ...prev };
      for (const p of validatableProducts) {
        if (p.moduleKey) {
          next[p.moduleKey] = { status: 'pending', blockers: [], organizationName: undefined };
        }
      }
      return next;
    });
    validateMutation.mutate({
      modules: validatableProducts
        .filter((p) => p.moduleKey !== null)
        .map((p) => ({ moduleKey: p.moduleKey as string, sourceId: p.labelId })),
    });
  }

  return (
    <div className="space-y-6">
      {data.entity && (
        <EntityHeader entity={data.entity} duplicationCount={data.duplicationWarnings.length} />
      )}

      <SummaryPanel counts={counts} />

      {data.duplicationWarnings.length > 0 && (
        <DuplicationCard warnings={data.duplicationWarnings} />
      )}

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <CardTitle className="text-lg">Product Migratability</CardTitle>
              <CardDescription>
                Each Bridge product maps to a Module configuration. This table shows which
                ones can migrate, why others can&apos;t, and lets you run a live validation
                against the source system.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <Select value={filter} onValueChange={(v) => setFilter(v as StatusFilter)}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Filter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All ({counts.total})</SelectItem>
                  <SelectItem value="migratable">Migratable ({counts.migratable})</SelectItem>
                  <SelectItem value="blocked">Blocked ({counts.blocked})</SelectItem>
                  <SelectItem value="special">Special ({counts.special})</SelectItem>
                  <SelectItem value="duplicate">Duplicate ({counts.duplicate})</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {data.products.length === 0 ? (
            <EmptyState
              title="No products found"
              description="This organization has no Bridge products."
            />
          ) : (
            <>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 pb-3 border-b">
                <p className="text-sm text-muted-foreground">
                  {validatableProducts.length === 0
                    ? 'No modules are eligible for live validation in this entity.'
                    : `${validatableProducts.length} module${validatableProducts.length === 1 ? '' : 's'} eligible for live validation against the source system.`}
                </p>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={runValidation}
                  disabled={
                    !canValidate ||
                    validatableProducts.length === 0 ||
                    validateMutation.isPending
                  }
                  title={
                    !canValidate
                      ? 'Operator role required to run validation'
                      : validatableProducts.length === 0
                        ? 'No eligible modules to validate'
                        : undefined
                  }
                >
                  {validateMutation.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                  )}
                  {validateMutation.isPending ? 'Validating…' : 'Validate all'}
                </Button>
              </div>

              {validateMutation.isError && (
                <Alert variant="destructive" className="mb-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Validation failed</AlertTitle>
                  <AlertDescription>
                    {(validateMutation.error as Error)?.message ??
                      'The validation request could not be completed.'}
                  </AlertDescription>
                </Alert>
              )}

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead>Migratability</TableHead>
                    <TableHead>Why</TableHead>
                    <TableHead>Validation</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredProducts.map((p, i) => {
                    const key = p.moduleKey;
                    const validationRow = key ? validation[key] : undefined;
                    const eligible = key !== null && validatableModuleKeys.has(key);
                    return (
                      <TableRow key={`${p.productType}-${p.labelId}-${i}`}>
                        <TableCell>
                          <div className="font-medium">{p.productName}</div>
                          <div className="text-xs text-muted-foreground mt-0.5">
                            <span className="font-mono">{p.productTypeName}</span>
                            {p.moduleKey && (
                              <>
                                <span className="px-1">·</span>
                                <span className="font-mono">{p.moduleKey}</span>
                              </>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <MigratabilityStatus product={p} />
                        </TableCell>
                        <TableCell>
                          <ProductIssues product={p} />
                        </TableCell>
                        <TableCell>
                          <ValidationCell row={validationRow} eligible={eligible} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {filteredProducts.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-6">
                  No products match the selected filter.
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {data.entity && (
        <UserMigrationDryRunCard
          bridgeEntityUuid={data.entity.uuid}
          canRun={canValidate}
        />
      )}
    </div>
  );
}

function EntityHeader({
  entity,
  duplicationCount,
}: {
  entity: BridgeEntityInfo;
  duplicationCount: number;
}) {
  const location = [entity.city, entity.state, entity.zip].filter(Boolean).join(', ');
  return (
    <Card>
      <CardHeader>
        <div className="flex items-start gap-3">
          <div className="rounded-md bg-muted p-2 shrink-0">
            <Building2 className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <CardTitle className="truncate">{entity.name}</CardTitle>
            <CardDescription className="font-mono text-xs mt-1 break-all">
              {entity.uuid}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
          <Field label="Location" value={location || '—'} />
          <Field
            label="Contact"
            value={entity.contactName ?? '—'}
            sub={entity.contactEmail ?? undefined}
          />
          <Field label="Products" value={String(entity.productCount)} />
        </div>

        {duplicationCount > 0 ? (
          <Alert variant="warning">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>
              {duplicationCount} duplication{duplicationCount === 1 ? '' : 's'} detected
            </AlertTitle>
            <AlertDescription>
              One or more products on this entity already exist on another Amplify
              organization, or appear more than once on this Bridge entity. Resolve these
              before starting a migration.
            </AlertDescription>
          </Alert>
        ) : (
          <Alert variant="success">
            <CheckCircle2 className="h-4 w-4" />
            <AlertTitle>No duplicates detected</AlertTitle>
            <AlertDescription>
              No conflicts found between this entity&apos;s products and existing Amplify
              organizations.
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

function Field({ label, value, sub }: { label: string; value: string; sub?: string | undefined }) {
  return (
    <div>
      <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
      <p className="font-medium mt-1">{value}</p>
      {sub && <p className="text-muted-foreground text-xs">{sub}</p>}
    </div>
  );
}

function SummaryPanel({ counts }: { counts: ProductCounts }) {
  const stats = [
    { label: 'Total products', value: counts.total, icon: GitBranch, tone: 'muted' as const },
    { label: 'Migratable', value: counts.migratable, icon: CheckCircle2, tone: 'success' as const },
    { label: 'Special', value: counts.special, icon: Sparkles, tone: 'info' as const },
    { label: 'Duplicate', value: counts.duplicate, icon: Copy, tone: 'warning' as const },
    { label: 'Blocked', value: counts.blocked, icon: Ban, tone: 'destructive' as const },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
      {stats.map((s) => {
        const Icon = s.icon;
        const toneClass = toneToClass(s.tone);
        return (
          <Card key={s.label}>
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground uppercase tracking-wide">
                  {s.label}
                </span>
                <Icon className={`h-4 w-4 ${toneClass}`} />
              </div>
              <p className={`text-2xl font-semibold mt-1 ${toneClass}`}>{s.value}</p>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function DuplicationCard({ warnings }: { warnings: DuplicationWarning[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg text-warning">
          <AlertTriangle className="h-5 w-5" />
          Duplication Warnings ({warnings.length})
        </CardTitle>
        <CardDescription>
          <span className="font-medium text-foreground">Cross-Entity</span> means the same
          product is already attached to another Bridge entity that has migrated.{' '}
          <span className="font-medium text-foreground">Intra-Entity</span> means the same
          product type appears more than once on this entity.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Type</TableHead>
              <TableHead>Product</TableHead>
              <TableHead>Detail</TableHead>
              <TableHead>Conflicting Entity</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {warnings.map((w, i) => (
              <TableRow key={`${w.type}-${w.productType}-${i}`}>
                <TableCell>
                  {w.type === 'CrossEntity' ? (
                    <div className="flex items-center gap-1.5 text-destructive">
                      <AlertTriangle className="h-4 w-4 shrink-0" />
                      <span className="text-sm font-medium">Cross-Entity</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5 text-warning">
                      <Copy className="h-4 w-4 shrink-0" />
                      <span className="text-sm font-medium">Intra-Entity</span>
                    </div>
                  )}
                </TableCell>
                <TableCell className="font-medium">{w.productName}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{w.description}</TableCell>
                <TableCell>
                  {w.conflictingEntityName ? (
                    <div>
                      <p className="font-medium text-sm">{w.conflictingEntityName}</p>
                      {w.conflictingEntityUuid && (
                        <p className="font-mono text-xs text-muted-foreground">
                          {w.conflictingEntityUuid}
                        </p>
                      )}
                    </div>
                  ) : (
                    '—'
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function MigratabilityStatus({ product }: { product: ProductMigratability }) {
  const entry = {
    Yes: { icon: CheckCircle2, label: 'Migratable', tone: 'success' as const },
    Special: { icon: Sparkles, label: 'Special', tone: 'info' as const },
    Duplicate: { icon: Copy, label: 'Duplicate', tone: 'warning' as const },
    No: { icon: Ban, label: 'Not migratable', tone: 'destructive' as const },
  }[product.migratabilityStatus];
  const Icon = entry.icon;
  return (
    <div className={`flex items-center gap-1.5 ${toneToClass(entry.tone)}`}>
      <Icon className="h-4 w-4 shrink-0" />
      <span className="text-sm font-medium">{entry.label}</span>
    </div>
  );
}

function ProductIssues({ product }: { product: ProductMigratability }) {
  if (product.isSpecialModule && product.migratabilityStatus === 'Special') {
    return (
      <span className="text-muted-foreground text-sm">
        Handled outside the orchestrator
      </span>
    );
  }

  const issues: string[] = [];
  if (!product.hasModuleConfig) {
    issues.push('No module configuration registered for this product type');
  }
  if (product.hasModuleConfig && !product.isEnabled) {
    issues.push('Module is disabled in admin settings');
  }
  if (product.hasModuleConfig && !product.isSpecialModule && !product.hasMigrateEndpoint) {
    issues.push('Module config is missing a migrate endpoint');
  }
  if (product.hasDuplicationIssue) {
    issues.push('Product already exists on another organization');
  }

  if (issues.length === 0) {
    return <span className="text-muted-foreground text-sm">—</span>;
  }

  return (
    <ul className="space-y-1">
      {issues.map((issue) => (
        <li key={issue} className="flex items-start gap-1.5 text-sm">
          <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-muted-foreground" />
          <span>{issue}</span>
        </li>
      ))}
    </ul>
  );
}

function ValidationCell({
  row,
  eligible,
}: {
  row: ValidationRow | undefined;
  eligible: boolean;
}) {
  if (!eligible) {
    return <span className="text-muted-foreground text-sm">Not eligible</span>;
  }
  if (!row) {
    return <span className="text-muted-foreground text-sm">Not run</span>;
  }
  if (row.status === 'pending') {
    return (
      <div className="flex items-center gap-1.5 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm">Validating…</span>
      </div>
    );
  }
  if (row.status === 'valid') {
    return (
      <div className="flex flex-col gap-0.5">
        <div className="flex items-center gap-1.5 text-success">
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          <span className="text-sm font-medium">Valid</span>
        </div>
        {row.organizationName && (
          <span className="text-xs text-muted-foreground">{row.organizationName}</span>
        )}
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1.5 text-destructive">
        <Ban className="h-4 w-4 shrink-0" />
        <span className="text-sm font-medium">Blocked</span>
      </div>
      {row.blockers.length > 0 && (
        <ul className="space-y-0.5 text-xs text-muted-foreground">
          {row.blockers.map((b) => (
            <li key={b} className="flex items-start gap-1">
              <span className="shrink-0">•</span>
              <span>{b}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function isValidatable(p: ProductMigratability): boolean {
  return (
    p.moduleKey !== null &&
    p.hasModuleConfig &&
    p.isEnabled &&
    p.hasMigrateEndpoint &&
    !p.isSpecialModule
  );
}

function filterToStatus(
  filter: StatusFilter,
): ProductMigratability['migratabilityStatus'] | null {
  switch (filter) {
    case 'migratable':
      return 'Yes';
    case 'blocked':
      return 'No';
    case 'special':
      return 'Special';
    case 'duplicate':
      return 'Duplicate';
    case 'all':
    default:
      return null;
  }
}

function toneToClass(tone: 'muted' | 'success' | 'info' | 'warning' | 'destructive'): string {
  switch (tone) {
    case 'success':
      return 'text-success';
    case 'info':
      return 'text-info';
    case 'warning':
      return 'text-warning';
    case 'destructive':
      return 'text-destructive';
    case 'muted':
    default:
      return 'text-muted-foreground';
  }
}

// ===========================================================================
// User-migration dry-run
// ===========================================================================

function UserMigrationDryRunCard({
  bridgeEntityUuid,
  canRun,
}: {
  bridgeEntityUuid: string;
  canRun: boolean;
}) {
  const mutation = useMutation({
    mutationFn: () => customerIntelApi.runUserMigrationDryRun(bridgeEntityUuid),
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Users className="h-5 w-5" />
              User-Migration Dry-Run
            </CardTitle>
            <CardDescription>
              Calls each eligible module&apos;s GET-users endpoint, runs the same
              cross-module merge + email dedup as a real migration, and surfaces
              identity-collision warnings (e.g. two emails sharing a phone number that
              Amplify would identity-merge on onboard). Read-only — no onboarding occurs.
            </CardDescription>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => mutation.mutate()}
            disabled={!canRun || mutation.isPending}
            title={!canRun ? 'Operator role required to run the dry-run' : undefined}
          >
            {mutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Users className="h-4 w-4 mr-2" />
            )}
            {mutation.isPending ? 'Running…' : 'Run dry-run'}
          </Button>
        </div>
      </CardHeader>

      {mutation.isError && (
        <CardContent>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Dry-run failed</AlertTitle>
            <AlertDescription>
              {(mutation.error as Error)?.message ?? 'The dry-run request could not be completed.'}
            </AlertDescription>
          </Alert>
        </CardContent>
      )}

      {mutation.data && (
        <CardContent>
          <DryRunReport result={mutation.data} />
        </CardContent>
      )}
    </Card>
  );
}

function DryRunReport({ result }: { result: UserMigrationDryRunResult }) {
  if (!result.found) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Entity not found</AlertTitle>
        <AlertDescription>
          The Bridge entity could not be resolved during the dry-run.
        </AlertDescription>
      </Alert>
    );
  }

  const hasIdentityCollisions = result.identityCollisions.length > 0;
  const hasAmplifyMatches = (result.amplifyMatches?.length ?? 0) > 0;
  const allModulesSucceeded = result.modulesQueried.every((m) => m.success);
  const noEligibleModules =
    result.modulesQueried.length === 0 && result.modulesSkipped.length === 0;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <DryRunStat label="Raw users" value={result.totalRawUsers} tone="muted" />
        <DryRunStat label="Unique after dedup" value={result.totalUniqueUsers} tone="info" />
        <DryRunStat
          label="Identity collisions"
          value={result.identityCollisions.length}
          tone={hasIdentityCollisions ? 'warning' : 'success'}
        />
        <DryRunStat
          label="Amplify matches"
          value={result.amplifyMatches?.length ?? 0}
          tone={hasAmplifyMatches ? 'warning' : 'muted'}
          subtle={result.amplifyMatches === null ? 'not run' : undefined}
        />
      </div>

      {noEligibleModules && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertTitle>No eligible modules</AlertTitle>
          <AlertDescription>
            None of this entity&apos;s products map to a module with user-migration
            endpoints configured. There&apos;s nothing for the dry-run to check.
          </AlertDescription>
        </Alert>
      )}

      {!noEligibleModules && (
        <DryRunModulesSection
          queried={result.modulesQueried}
          skipped={result.modulesSkipped}
          allSucceeded={allModulesSucceeded}
        />
      )}

      {hasIdentityCollisions && (
        <IdentityCollisionsSection collisions={result.identityCollisions} />
      )}

      {result.amplifyOrganizationUuid && result.amplifyMatches === null && (
        <Alert variant="warning">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Amplify match preview unavailable</AlertTitle>
          <AlertDescription>
            The Bridge entity is linked to Amplify org{' '}
            <span className="font-mono text-xs">{result.amplifyOrganizationUuid}</span>,
            but the user lookup against Amplify failed. Intra-batch results above are
            still valid.
          </AlertDescription>
        </Alert>
      )}

      {hasAmplifyMatches && result.amplifyMatches && (
        <AmplifyMatchesSection
          matches={result.amplifyMatches}
          amplifyOrgUuid={result.amplifyOrganizationUuid}
        />
      )}

      {!hasIdentityCollisions
        && !hasAmplifyMatches
        && !noEligibleModules
        && allModulesSucceeded && (
          <Alert variant="success">
            <CheckCircle2 className="h-4 w-4" />
            <AlertTitle>No issues detected</AlertTitle>
            <AlertDescription>
              All eligible modules returned users cleanly, no source rows share identity
              attributes that Amplify would merge on, and no incoming users match existing
              Amplify users on the linked org.
            </AlertDescription>
          </Alert>
      )}
    </div>
  );
}

function DryRunStat({
  label,
  value,
  tone,
  subtle,
}: {
  label: string;
  value: number;
  tone: 'muted' | 'info' | 'success' | 'warning' | 'destructive';
  subtle?: string | undefined;
}) {
  const toneClass = toneToClass(tone);
  return (
    <Card>
      <CardContent className="py-4">
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground uppercase tracking-wide">
            {label}
          </span>
        </div>
        <p className={`text-2xl font-semibold mt-1 ${toneClass}`}>{value}</p>
        {subtle && <p className="text-xs text-muted-foreground mt-0.5">{subtle}</p>}
      </CardContent>
    </Card>
  );
}

function DryRunModulesSection({
  queried,
  skipped,
  allSucceeded,
}: {
  queried: DryRunModuleResult[];
  skipped: DryRunSkippedModule[];
  allSucceeded: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="rounded-md border">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center justify-between px-4 py-3 hover:bg-muted/50"
      >
        <div className="flex items-center gap-2">
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          <span className="font-medium text-sm">
            Modules ({queried.length} queried, {skipped.length} skipped)
          </span>
          {!allSucceeded && (
            <span className="text-xs text-destructive">— one or more GETs failed</span>
          )}
        </div>
      </button>
      {expanded && (
        <div className="border-t px-4 py-3 space-y-3">
          {queried.length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
                Queried
              </p>
              <ul className="space-y-1 text-sm">
                {queried.map((m) => (
                  <li key={m.moduleKey} className="flex items-start gap-2">
                    {m.success ? (
                      <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-success" />
                    ) : (
                      <AlertCircle className="h-4 w-4 mt-0.5 shrink-0 text-destructive" />
                    )}
                    <div className="flex-1">
                      <span className="font-medium">{m.displayName}</span>
                      <span className="text-muted-foreground"> · </span>
                      <span className="font-mono text-xs text-muted-foreground">
                        {m.moduleKey}
                      </span>
                      {m.success ? (
                        <span className="text-muted-foreground">
                          {' '}— {m.usersFetched} user{m.usersFetched === 1 ? '' : 's'} fetched
                        </span>
                      ) : (
                        <span className="text-destructive"> — {m.errorMessage}</span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {skipped.length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
                Skipped
              </p>
              <ul className="space-y-1 text-sm">
                {skipped.map((s) => (
                  <li key={s.moduleKey} className="flex items-start gap-2">
                    <Info className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
                    <div className="flex-1">
                      <span className="font-medium">{s.displayName}</span>
                      <span className="text-muted-foreground"> · </span>
                      <span className="font-mono text-xs text-muted-foreground">
                        {s.moduleKey}
                      </span>
                      <span className="text-muted-foreground"> — {s.reason}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function IdentityCollisionsSection({
  collisions,
}: {
  collisions: IdentityCollisionWarning[];
}) {
  return (
    <Alert variant="warning">
      <AlertTriangle className="h-4 w-4" />
      <AlertTitle>
        {collisions.length} identity collision{collisions.length === 1 ? '' : 's'} detected
      </AlertTitle>
      <AlertDescription>
        <p className="mb-2 text-sm">
          These groups of source users share identity attributes that Amplify is known to
          identity-merge on. If you proceed with a real migration, Amplify will likely
          return the same UUID for each member of the group and one of them will end up
          attached to the other&apos;s Amplify account.
        </p>
        <ul className="space-y-3">
          {collisions.map((c, i) => (
            <li
              key={`${c.collisionType}-${c.sharedPhone ?? 'na'}-${i}`}
              className="rounded border bg-background p-3"
            >
              <p className="text-sm font-medium text-foreground">{c.description}</p>
              <ul className="mt-2 space-y-0.5 text-sm">
                {c.affectedEmails.map((email) => (
                  <li
                    key={email}
                    className="flex items-center gap-1.5 font-mono text-xs text-muted-foreground"
                  >
                    <span>•</span>
                    <span>{email}</span>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </AlertDescription>
    </Alert>
  );
}

function AmplifyMatchesSection({
  matches,
  amplifyOrgUuid,
}: {
  matches: AmplifyMatchWarning[];
  amplifyOrgUuid: string | null;
}) {
  return (
    <Alert variant="warning">
      <AlertTriangle className="h-4 w-4" />
      <AlertTitle>
        {matches.length} incoming user{matches.length === 1 ? '' : 's'} match{matches.length === 1 ? 'es' : ''} an existing Amplify user
      </AlertTitle>
      <AlertDescription>
        <p className="mb-2 text-sm">
          The Bridge entity is already linked to Amplify org{' '}
          {amplifyOrgUuid && <span className="font-mono text-xs">{amplifyOrgUuid}</span>}.
          A real migration would attempt to onboard these users but Amplify will return
          the existing UUIDs instead, leading to the same merge behavior covered by PR #1.
        </p>
        <ul className="space-y-2">
          {matches.map((m) => (
            <li key={`${m.incomingEmail}-${m.matchedAmplifyUuid}`} className="text-sm">
              <span className="font-mono text-xs">{m.incomingEmail}</span>
              <span className="text-muted-foreground"> would resolve to </span>
              <span className="font-mono text-xs">{m.matchedAmplifyUuid}</span>
              {m.matchedEmail && (
                <>
                  <span className="text-muted-foreground"> ({m.matchedEmail})</span>
                </>
              )}
              <span className="text-muted-foreground"> via </span>
              <span className="font-medium">{m.matchReason}</span>
              <span className="text-muted-foreground"> match</span>
            </li>
          ))}
        </ul>
      </AlertDescription>
    </Alert>
  );
}
